// src/components/TZMMS-INV/panels/InventoryListPanel.jsx
// ============================================================================
// TZMMS Inventory Counter — main list box panel
// ----------------------------------------------------------------------------
// PURPOSE
// - Render the blank-to-filled inventory list box.
// - Keep the + button anchored in the bottom-left corner as requested.
// - Provide basic row editing helpers (select, remove selected, clear all).
// ============================================================================

import React from 'react';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  INVENTORY_LIST_PANEL_HEIGHT_DESKTOP,
  INVENTORY_LIST_PANEL_HEIGHT_MOBILE,
  INVENTORY_LIST_ROW_MIN_HEIGHT,
} from '../constants/tzmmsInvUi.js';

export default function InventoryListPanel({
  entries,
  selectedEntryId,
  onSelectEntry,
  onOpenScanner,
  onRemoveSelected,
  onClearAll,
}) {
  const safeEntries = Array.isArray(entries) ? entries : []; // Prevent crashes from unexpected null values.
  const hasEntries = safeEntries.length > 0; // Reused by multiple buttons + blank state.

  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ===================================================================== */}
      {/* BLOCK TZI.LIST.001 — Panel header */}
      {/* ===================================================================== */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="h6">Scanned Inventory Values</Typography>
        <Typography variant="body2" color="text.secondary">
          Use the + button to open the barcode scanner (QR, Data Matrix, Aztec, EAN-13, Code128).
        </Typography>
      </Box>

      <Divider />

      {/* ===================================================================== */}
      {/* BLOCK TZI.LIST.002 — Scrollable list box */}
      {/* ===================================================================== */}
      <Box
        sx={{
          flex: 1,
          minHeight: {
            xs: INVENTORY_LIST_PANEL_HEIGHT_MOBILE,
            md: INVENTORY_LIST_PANEL_HEIGHT_DESKTOP,
          },
          maxHeight: {
            xs: INVENTORY_LIST_PANEL_HEIGHT_MOBILE,
            md: INVENTORY_LIST_PANEL_HEIGHT_DESKTOP,
          },
          overflowY: 'auto', // Allows long sessions without blowing out layout height.
          overflowX: 'hidden',
        }}
      >
        {!hasEntries && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100%', px: 2, textAlign: 'center' }}
          >
            <Typography variant="body1">List is empty.</Typography>
            <Typography variant="body2" color="text.secondary">
              Tap the + button below and scan one or more barcodes (QR, Data Matrix, Aztec, EAN-13,
              Code128).
            </Typography>
          </Stack>
        )}

        {hasEntries && (
          <List disablePadding>
            {safeEntries.map((entry, index) => {
              const isSelected = entry?.id === selectedEntryId; // Highlight the currently selected row.

              return (
                <ListItemButton
                  key={entry?.id || `entry-${index}`}
                  selected={isSelected}
                  onClick={() => onSelectEntry(entry?.id || null)}
                  sx={{
                    minHeight: INVENTORY_LIST_ROW_MIN_HEIGHT,
                    alignItems: 'flex-start',
                    borderLeft: isSelected ? '4px solid' : '4px solid transparent',
                    borderColor: 'primary.main',
                  }}
                >
                  <ListItemText
                    primary={`${index + 1}. ${entry?.rawValue || ''}`}
                    primaryTypographyProps={{
                      sx: {
                        wordBreak: 'break-word', // Long QR values should wrap cleanly.
                        fontFamily: 'monospace',
                      },
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>

      <Divider />

      {/* ===================================================================== */}
      {/* BLOCK TZI.LIST.003 — Footer actions */}
      {/* ===================================================================== */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="stretch"
          spacing={1}
        >
          <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={onOpenScanner}>
            +
          </Button>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<DeleteOutlineIcon />}
              onClick={onRemoveSelected}
              disabled={!selectedEntryId}
              fullWidth
            >
              Remove Selected
            </Button>

            <Button
              variant="outlined"
              color="error"
              startIcon={<ClearAllIcon />}
              onClick={onClearAll}
              disabled={!hasEntries}
              fullWidth
            >
              Clear All
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}
